/**
 * The open row of the desktop week grid changing, animated (see rowFocus.ts).
 *
 * React swaps a strip for a full card in one commit, so without this the grid
 * jumps: dinner collapses to 50px and lunch appears at 440px in the same frame.
 * Instead each row that changed height is clipped and animated from the height
 * it had to the height it has now (a FLIP on the row box), and its cells fade
 * in, so the opening row unfolds while the closing one folds.
 *
 * Nothing is laid out twice: the cells are already at their final size, the
 * row box only reveals them. Web only — the grid is CSS Grid — and skipped
 * when the cook asked for reduced motion.
 */

export const ROW_ANIMATION_MS = 450;
/** The strips of the closing row settle faster than the cards that open. */
const CLOSING_FADE_MS = 320;
/**
 * A soft ease-in-out (easeInOutSine): it leaves the click without a jolt and
 * settles without a snap. A sharper ease-out at 300ms read as abrupt.
 */
const EASING = 'cubic-bezier(0.37, 0, 0.63, 1)';

/** The bits of an HTMLElement this needs, so the test can hand it a fake. */
export interface AnimatableRow {
  style: { overflow: string };
  children: ArrayLike<unknown>;
  animate: (keyframes: Keyframe[], options: KeyframeAnimationOptions) => {
    onfinish: (() => void) | null;
    oncancel: (() => void) | null;
  };
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Animates every row whose height changed between two commits. `opened` is the
 * row that just opened: its cells rise slightly as they fade in, the strips of
 * the row that closed fade in faster. Returns how many rows it animated.
 */
export function animateRowSwap(
  rows: ReadonlyArray<AnimatableRow | null | undefined>,
  from: readonly number[],
  to: readonly number[],
  opened: number,
): number {
  let animated = 0;
  rows.forEach((row, i) => {
    if (!row || typeof row.animate !== 'function') return;
    const a = from[i];
    const b = to[i];
    const moved = a > 0 && b > 0 && Math.abs(a - b) >= 1;
    if (!moved) return;

    // Clip while the box is between sizes: the cells are already at their
    // final size and must not spill over the next row. Released at the end,
    // because the hover offer under a card floats outside its row.
    row.style.overflow = 'hidden';
    // min-height too: the open row carries its height as an inline
    // min-height, which would hold the box at full size from the first frame.
    const animation = row.animate(
      [{ height: `${a}px`, minHeight: `${a}px` }, { height: `${b}px`, minHeight: `${b}px` }],
      { duration: ROW_ANIMATION_MS, easing: EASING },
    );
    const release = () => { row.style.overflow = ''; };
    animation.onfinish = release;
    animation.oncancel = release;

    // The cells start part-visible, never at 0: starting from nothing left a
    // frame with both rows blank, which read as a flash rather than a fold.
    const isOpening = i === opened;
    Array.from(row.children as ArrayLike<AnimatableRow>).forEach((cell) => {
      if (typeof cell?.animate !== 'function') return;
      cell.animate(
        isOpening
          ? [{ opacity: 0.25, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }]
          : [{ opacity: 0.4 }, { opacity: 1 }],
        { duration: isOpening ? ROW_ANIMATION_MS : CLOSING_FADE_MS, easing: EASING },
      );
    });
    animated += 1;
  });
  return animated;
}
